import { useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Container,
  Group,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconPencil, IconPlus, IconUsers } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import type { MyGroupEntry } from '@pta/types';
import { useAuth } from '../auth/AuthProvider';
import { GroupCreateModal } from '../components/GroupCreateModal';
import { GroupEditModal } from '../components/GroupEditModal';

/**
 * "Saját csoportjaim" — minden auth user látja.
 *
 * Funkciók:
 *  - Lista a `myGroups`-ból (AuthProvider).
 *  - "Új csoport" gomb → GroupCreateModal (free_text path).
 *  - Sor-ban "Szerkesztés" gomb → GroupEditModal (csak ahol leader/admin scope-os).
 *  - Sor-ban "Diákok" gomb → navigál a /app/groups/:id/students-re.
 *  - Sor-ban "Megnyitás" → setActiveGroupId + redirect /app-ra (financial dashboard).
 *
 * A modal create után `refresh()`-eli az AuthProvider-t, hogy az új csoport
 * megjelenjen, és aktívvá tegyük.
 */
export function MyGroupsPage() {
  const { myGroups, refresh, setActiveGroupId } = useAuth();
  const navigate = useNavigate();
  const [createOpened, setCreateOpened] = useState(false);
  const [editTarget, setEditTarget] = useState<MyGroupEntry | null>(null);

  return (
    <Container size="lg">
      <Stack>
        <Group justify="space-between" align="end">
          <Title order={2}>Saját csoportjaim</Title>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateOpened(true)}
          >
            Új csoport
          </Button>
        </Group>

        {myGroups.length === 0 ? (
          <Card withBorder>
            <Stack align="center" gap="xs" py="md">
              <Text c="dimmed">Még nincs csoportod.</Text>
              <Button variant="light" onClick={() => setCreateOpened(true)}>
                Hozz létre egyet
              </Button>
            </Stack>
          </Card>
        ) : (
          <Card withBorder p={0}>
            <Table striped highlightOnHover withColumnBorders={false}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Csoport</Table.Th>
                  <Table.Th>Iskola</Table.Th>
                  <Table.Th>Státusz</Table.Th>
                  <Table.Th style={{ width: 220 }}>Műveletek</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {myGroups.map((g) => (
                  <Table.Tr key={g.id}>
                    <Table.Td>
                      <Text fw={500}>{g.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{g.institutionLabel ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <BindingStatusBadge status={g.institutionBindingStatus} />
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Tooltip label="Diákok">
                          <ActionIcon
                            variant="light"
                            onClick={() => {
                              setActiveGroupId(g.id);
                              navigate(`/app/groups/${g.id}/students`);
                            }}
                          >
                            <IconUsers size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label="Szerkesztés">
                          <ActionIcon
                            variant="light"
                            onClick={() => setEditTarget(g)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                        </Tooltip>
                        <Button
                          size="xs"
                          variant="subtle"
                          onClick={() => {
                            setActiveGroupId(g.id);
                            navigate('/app');
                          }}
                        >
                          Megnyitás
                        </Button>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Card>
        )}
      </Stack>

      <GroupCreateModal
        opened={createOpened}
        onClose={() => setCreateOpened(false)}
        onCreated={async (newId) => {
          setCreateOpened(false);
          await refresh();
          setActiveGroupId(newId);
        }}
      />

      {editTarget && (
        <GroupEditModal
          opened={true}
          onClose={() => setEditTarget(null)}
          groupEntry={editTarget}
          onSaved={async () => {
            setEditTarget(null);
            await refresh();
          }}
        />
      )}
    </Container>
  );
}

function BindingStatusBadge({
  status,
}: {
  status: MyGroupEntry['institutionBindingStatus'];
}) {
  switch (status) {
    case 'free_text':
      return (
        <Badge color="gray" variant="light">
          Szabad szöveges
        </Badge>
      );
    case 'claimed':
      return (
        <Badge color="yellow" variant="light">
          Igényelt
        </Badge>
      );
    case 'verified':
      return (
        <Badge color="teal" variant="light">
          Hitelesített
        </Badge>
      );
    default:
      return null;
  }
}
